import type { PaymentDueScope, PaymentKind, PaymentRecipientKind, PaymentSheet } from "@shyne/shared/api";
import { api } from "@/lib/api";
import { activeOrganizationId } from "@/lib/active-organization";

export type StripePaymentMethodType = "card" | "us_bank_account";
export type StripePaymentKind = PaymentKind;
export type PaymentScope = "appointments" | "daily_work" | "both";

export interface PaymentDateRange {
  fromDate?: string | null;
  toDate?: string | null;
}

export type PaymentRecipientType = "individual" | "subs";

export interface CreatePaymentSheetPayload {
  amount: number;
  description?: string;
  recipientType: PaymentRecipientType;
  recipientId: string;
  paymentMethodType: StripePaymentMethodType;
  kind?: StripePaymentKind;
  paymentScope?: PaymentScope;
  dateRange?: PaymentDateRange;
}

export interface CreatePaymentSheetResponse {
  clientSecret: string | null;
  paymentIntentId: string;
  amount: number;
  currency: string;
  status: string;
  customerId: string | null;
  ephemeralKey: string | null;
  applicationFeeAmount: number;
  netTransferAmount: number;
  paymentMethodType: StripePaymentMethodType;
  paymentAttemptId: string;
}

export const RECIPIENT_KIND: Record<PaymentRecipientType, PaymentRecipientKind> = { individual: "worker", subs: "subcontractor" };

export const DUE_SCOPE: Record<PaymentScope, PaymentDueScope> = { appointments: "appointments", daily_work: "workDays", both: "both" };

export const payerParties = (dateRange?: PaymentDateRange, paymentScope: PaymentScope = "both") => {
  const payer = activeOrganizationId();
  return {
    payer,
    actingOrganization: payer,
    ...(dateRange?.fromDate ? { from: dateRange.fromDate } : {}),
    ...(dateRange?.toDate ? { to: dateRange.toDate } : {}),
    scope: DUE_SCOPE[paymentScope],
  };
};

const toResponse = (sheet: PaymentSheet): CreatePaymentSheetResponse => ({
  clientSecret: sheet.clientSecret,
  paymentIntentId: sheet.paymentIntent,
  amount: sheet.amountCents,
  currency: sheet.currency,
  status: sheet.status,
  customerId: sheet.customer,
  ephemeralKey: sheet.ephemeralKey,
  applicationFeeAmount: sheet.applicationFeeCents,
  netTransferAmount: sheet.netTransferCents,
  paymentMethodType: sheet.paymentMethodType,
  paymentAttemptId: sheet.attempt,
});

export async function createPaymentSheet(payload: CreatePaymentSheetPayload): Promise<CreatePaymentSheetResponse> {
  const sheet = await api.paymentDues.openPaymentSheet(RECIPIENT_KIND[payload.recipientType], payload.recipientId, {
    ...payerParties(payload.dateRange, payload.paymentScope),
    kind: payload.kind ?? "due",
    amountCents: payload.amount,
    paymentMethodType: payload.paymentMethodType,
    ...(payload.description ? { description: payload.description } : {}),
  });
  return toResponse(sheet);
}
